/** @jsxRuntime classic */
import { useContext, useRef, useState } from 'react';
import ToolbarExtension from '~components/atoms/Viewers/SVFViewer/extensions/ToolbarExtension';
import {
  DbFile,
  DbModuleFile,
  DbTenderInsideSVFFile,
  DbTenderSVFAttachment,
} from '~src/types/db';
import { useProjectId } from '../useProjectId';
import { apiCweb } from '~src/services/api/api-cweb';
import { appLog, debugLog } from '~src/utils/log';
import FileVersionsContext, {
  FileVersionSelection,
} from '~src/contexts/FileVersionsContext';
import { useIntl } from 'react-intl';
import { useQueryCache } from 'react-query';
import {
  collectViewables as buildViewables,
  getInitialNode,
  loadViewableByGuid as loadByGuid,
  ViewableInfo,
} from '~src/hooks/svf-viewer-hooks/useViewables';

export type { ViewableInfo } from '~src/hooks/svf-viewer-hooks/useViewables';

export const svfModelState = {
  1: { code: 'DRAFT', description: 'Job created and queued' },
  2: { code: 'PARTIAL', description: 'File transferring to Autodesk' },
  4: { code: 'ACCEPTED', description: 'SVF2 conversion in progress' },
  7: { code: 'PROCESSED', description: 'Conversion successful' },
  5: { code: 'REJECTED', description: 'Conversion failed' },
};

const queueStages = {
  1: { code: 'PENDING', description: 'Pending' },
  2: { code: 'DOWNLOAD_S3', description: 'Downloading from AWS S3' },
  3: { code: 'UPLOAD_OOS', description: 'Uploading to Autodesk OOS' },
  4: { code: 'CONVERT_SVF2', description: 'Conversion in progress' },
  5: { code: 'COMPLETED', description: 'Completed' },
};

export type Viewer3DType = Autodesk.Viewing.GuiViewer3D | null;

export type ExtensionSVFModel = {
  file_id: string;
  state: number;
  autodesk_bucket_region: string;
  autodesk_object_id: string;
};

export type TranscribeStatus = {
  svf_model_state: number;
  queue_stage: number;
  translate_svf_progress: number;
  created_at: number;
  download_source_file_start_at: number;
  upload_source_file_end_at: number;
  translate_svf_end_at: number;
  download_source_file_end_at: number;
};

export type ViewerData = { access_token: string; urn: string };

// ==supported viewer sources
// Keep this union small and explicit.
// If SVF viewer is added to another source later, add it here first.
export type SVFSupportedFile =
  | DbFile
  | DbModuleFile
  | DbTenderSVFAttachment
  | DbTenderInsideSVFFile;

// ==internal source kinds
// These values are only for this hook's internal branching.
type ViewerSourceKind =
  | 'archive_versioned_file'
  | 'tender_root_attachment'
  | 'tender_folder_versioned_file'
  | 'unassigned_tender_folder_versioned_file'
  | 'tender_share_root_attachment'
  | 'tender_share_folder_versioned_file';

// ==normalized version-like shape
// Existing SVF UI expects something close to a file version object.
// Tender root attachments do not have real versions, so we adapt them to this shape.
type ResolvedVersionLike = {
  id: string;
  file_id?: string | null;
  versioned_file_id?: number | null;
  name: string;
  version: string | null;
  revision: number | null;
  updated_at?: number | null;
  description?: string | null;
  registration_instance_description?: string | null;
  extension_svf_model?: ExtensionSVFModel | null;
  is_svf_convertible: boolean;
};

// ==resolved source adapter
// Central place for source-specific SVF behavior.
// If we add SVF support to another table/source later, add one more branch in
// `resolveViewerSource` instead of scattering source checks through the hook.
type ResolvedViewerSource = {
  kind: ViewerSourceKind;
  version: ResolvedVersionLike | null;
  fileName: string;
  extension?: string;
  canPatchContext: boolean;
  getTranscribeUrl: () => string | null;
  getStatusUrl: () => string | null;
  getLocationUrl: () => string | null;
  getHeaders?: () => Record<string, string>;
};

// ==source guards
const isTenderRootSVFAttachment = (
  fileToView?: SVFSupportedFile
): fileToView is DbTenderSVFAttachment => {
  return (
    !!fileToView && (fileToView as any).sourceType === 'tender-admin-root-svf'
  );
};

const isTenderFolderSVFFile = (
  fileToView?: SVFSupportedFile
): fileToView is DbTenderInsideSVFFile => {
  return (
    !!fileToView && (fileToView as any).sourceType === 'tender-admin-inside-svf'
  );
};

const isUnassignedTenderFolderSVFFile = (
  fileToView?: SVFSupportedFile
): fileToView is DbTenderInsideSVFFile => {
  return (
    !!fileToView &&
    (fileToView as any).sourceType === 'tender-unassigned-inside-svf'
  );
};

const isTenderShareRootSVFAttachment = (
  fileToView?: SVFSupportedFile
): fileToView is DbTenderSVFAttachment => {
  return (
    !!fileToView && (fileToView as any).sourceType === 'tender-share-root-svf'
  );
};

const isTenderShareFolderSVFFile = (
  fileToView?: SVFSupportedFile
): fileToView is DbTenderInsideSVFFile => {
  return (
    !!fileToView && (fileToView as any).sourceType === 'tender-share-inside-svf'
  );
};

// ==shared helpers
const getDirectFileName = (fileToView?: SVFSupportedFile) => {
  return (
    (fileToView as any)?.title ||
    (fileToView as any)?.file_name ||
    (fileToView as any)?.name ||
    'undefined'
  );
};

const getExtensionFromFileName = (fileName?: string) => {
  const nameSections = fileName?.split('.');
  return nameSections && nameSections.length
    ? nameSections[nameSections.length - 1].toLowerCase()
    : undefined;
};

// ==tender root attachment adapter
// Root attachments have no real versioning, but the existing SVF UI expects
// version-like fields for state, title and progress handling.
const buildTenderAttachmentVersion = (
  fileToView: DbTenderSVFAttachment
): ResolvedVersionLike => {
  return {
    id: String(fileToView.id),
    file_id: String(fileToView.id),
    versioned_file_id: null,
    name:
      fileToView.file_name ||
      fileToView.title ||
      (fileToView as any).name ||
      'undefined',
    version: null,
    revision: (fileToView as any).serial_number ?? 1,
    updated_at: (fileToView as any).updated_at ?? null,
    description: (fileToView as any).description ?? null,
    registration_instance_description: (fileToView as any).description ?? null,
    extension_svf_model: (fileToView as any).extension_svf_model ?? null,
    is_svf_convertible: (fileToView as any).is_svf_convertible === true,
  };
};

const buildTenderFolderVersion = (
  fileToView: DbTenderInsideSVFFile
): ResolvedVersionLike => {
  return {
    id: String(fileToView.file_guid || fileToView.id),
    file_id: String(fileToView.file_guid || fileToView.id),
    versioned_file_id: Number(fileToView.versioned_file_id),
    name:
      fileToView.file_name ||
      fileToView.title ||
      (fileToView as any).name ||
      'undefined',
    version: null,
    revision: (fileToView as any).serial_number ?? 1,
    updated_at: (fileToView as any).updated_at ?? null,
    description: (fileToView as any).description ?? null,
    registration_instance_description: (fileToView as any).description ?? null,
    extension_svf_model: (fileToView as any).extension_svf_model ?? null,
    is_svf_convertible: (fileToView as any).is_svf_convertible === true,
  };
};

// ==main source resolver
// Source onboarding checklist:
// 1) extend SVFSupportedFile
// 2) add a source guard if needed
// 3) add one more branch here with version adapter + urls
const resolveViewerSource = (
  fileToView: SVFSupportedFile | undefined,
  fileVersionsContext: Record<string | number, any>,
  projectId: number | string | undefined
): ResolvedViewerSource | null => {
  if (!fileToView) return null;

  // ==tender root attachment source
  if (isTenderRootSVFAttachment(fileToView)) {
    const fileGuid = String(fileToView.id);
    const tenderId = Number((fileToView as any).tender_id);
    const fileName = getDirectFileName(fileToView);

    return {
      kind: 'tender_root_attachment',
      version: buildTenderAttachmentVersion(fileToView),
      fileName,
      extension: getExtensionFromFileName(fileName),
      canPatchContext: false,
      getTranscribeUrl: () =>
        `/projects/${projectId}/tenders/${tenderId}/svf_viewer_attachments/${fileGuid}?action=convert_to_svf2`,
      getStatusUrl: () =>
        `/projects/${projectId}/tenders/${tenderId}/svf_viewer_attachments/${fileGuid}/transcribe_status`,
      getLocationUrl: () =>
        `/projects/${projectId}/tenders/${tenderId}/svf_viewer_attachments/${fileGuid}/location`,
    };
  }

  // ==tender folder versioned-file source
  if (isTenderFolderSVFFile(fileToView)) {
    const fileName = getDirectFileName(fileToView);
    const tenderId = Number((fileToView as any).tender_id);
    const rootFolderId = Number((fileToView as any).root_folder_id);
    const folderId = Number((fileToView as any).folder_id);
    const versionedFileId = Number((fileToView as any).versioned_file_id);
    const fileGuid = String((fileToView as any).file_guid);

    return {
      kind: 'tender_folder_versioned_file',
      version: buildTenderFolderVersion(fileToView),
      fileName,
      extension: getExtensionFromFileName(fileName),
      canPatchContext: false,
      getTranscribeUrl: () => null,
      getStatusUrl: () => null,
      getLocationUrl: () =>
        `/projects/${projectId}/tenders/${tenderId}/tender_folders/${rootFolderId}/folders/${folderId}/versioned_files/${versionedFileId}/svf_viewer_files/${fileGuid}/location`,
    };
  }

  // ==unassigned tender folder versioned-file source
  if (isUnassignedTenderFolderSVFFile(fileToView)) {
    const fileName = getDirectFileName(fileToView);
    const rootFolderId = Number((fileToView as any).root_folder_id);
    const folderId = Number((fileToView as any).folder_id);
    const versionedFileId = Number((fileToView as any).versioned_file_id);
    const fileGuid = String((fileToView as any).file_guid);

    return {
      kind: 'unassigned_tender_folder_versioned_file',
      version: buildTenderFolderVersion(fileToView),
      fileName,
      extension: getExtensionFromFileName(fileName),
      canPatchContext: false,
      getTranscribeUrl: () => null,
      getStatusUrl: () => null,
      getLocationUrl: () =>
        `/projects/${projectId}/tender_folders/${rootFolderId}/folders/${folderId}/versioned_files/${versionedFileId}/svf_viewer_files/${fileGuid}/location`,
    };
  }

  // ==vendor share root attachment source
  if (isTenderShareRootSVFAttachment(fileToView)) {
    const fileGuid = String((fileToView as any).file_guid || fileToView.id);
    const shareId = String((fileToView as any).share_id);
    const shareKey = String((fileToView as any).share_key);
    const fileName = getDirectFileName(fileToView);

    return {
      kind: 'tender_share_root_attachment',
      version: buildTenderAttachmentVersion(fileToView),
      fileName,
      extension: getExtensionFromFileName(fileName),
      canPatchContext: false,
      getTranscribeUrl: () => null,
      getStatusUrl: () => null,
      getLocationUrl: () =>
        `/tender_shares/${shareId}/svf_viewer_attachments/${fileGuid}/location`,
      getHeaders: () => ({
        'Share-Key': shareKey,
      }),
    };
  }

  // ==vendor share tender folder versioned-file source
  if (isTenderShareFolderSVFFile(fileToView)) {
    const fileName = getDirectFileName(fileToView);
    const shareId = String((fileToView as any).share_id);
    const shareKey = String((fileToView as any).share_key);
    const rootFolderId = Number((fileToView as any).root_folder_id);
    const folderId = Number((fileToView as any).folder_id);
    const versionedFileId = Number((fileToView as any).versioned_file_id);
    const fileGuid = String((fileToView as any).file_guid);

    return {
      kind: 'tender_share_folder_versioned_file',
      version: buildTenderFolderVersion(fileToView),
      fileName,
      extension: getExtensionFromFileName(fileName),
      canPatchContext: false,
      getTranscribeUrl: () => null,
      getStatusUrl: () => null,
      getLocationUrl: () =>
        `/tender_shares/${shareId}/tender_folders/${rootFolderId}/folders/${folderId}/versioned_files/${versionedFileId}/svf_viewer_files/${fileGuid}/location`,
      getHeaders: () => ({
        'Share-Key': shareKey,
      }),
    };
  }

  // ==default archive / versioned-file source
  const version =
    fileToView != null ? fileVersionsContext[fileToView.id] : null;
  const fileName = getDirectFileName(fileToView);

  return {
    kind: 'archive_versioned_file',
    version: version ?? null,
    fileName,
    extension: version?.name
      ? getExtensionFromFileName(version.name)
      : getExtensionFromFileName(fileName),
    canPatchContext: true,
    getTranscribeUrl: () => {
      if (!version?.versioned_file_id || !version?.id) return null;
      return `/projects/${projectId}/versioned_files/${version.versioned_file_id}/svf_viewer_files/${version.id}?action=convert_to_svf2`;
    },
    getStatusUrl: () => {
      if (!version?.versioned_file_id || !version?.id) return null;
      return `/projects/${projectId}/versioned_files/${version.versioned_file_id}/svf_viewer_files/${version.id}/transcribe_status`;
    },
    getLocationUrl: () => {
      if (!version?.versioned_file_id || !version?.id) return null;
      return `/projects/${projectId}/versioned_files/${version.versioned_file_id}/svf_viewer_files/${version.id}/location`;
    },
  };
};

export const useSVFViewer = (fileToView?: SVFSupportedFile) => {
  const intl = useIntl();
  const { fileVersionsContext, setFileVersionsContext } = useContext(
    FileVersionsContext
  );
  const projectId = useProjectId();
  const queryCache = useQueryCache();

  // ==resolve current file source once through the adapter layer
  const resolvedSource = resolveViewerSource(
    fileToView,
    fileVersionsContext as Record<string | number, any>,
    projectId
  );

  const version = resolvedSource?.version ?? null;
  const extension = resolvedSource?.extension;
  const fileName = resolvedSource?.fileName || 'undefined';

  const [viewer3D, setViewer3D] = useState<Viewer3DType>(null);

  // Keep Document & viewables
  const [
    viewerDocument,
    setViewerDocument,
  ] = useState<Autodesk.Viewing.Document | null>(null);
  const [viewables, setViewables] = useState<ViewableInfo[]>([]);

  const abortRef = useRef(false);

  const [status, setStatus] = useState({
    loading: false,
    error: false,
    message: '',
    progress: 0,
  });

  // Fake progress counter for when Autodesk returns 0% for extended periods
  const fakeProgressCounterRef = useRef(0);

  // Helper: keep DS_ENDPOINTS on (EU/GDPR); do not force api/region.
  const enableEUAutoRouting = () => {
    try {
      Autodesk.Viewing.FeatureFlags.set('DS_ENDPOINTS', true);
      console.log(`[SVFViewer] DS_ENDPOINTS enabled`);
    } catch {}
  };

  /** Init viewer with EU auto-routing and token callback (do NOT set `api`) */
  const initViewer = async (
    access_token: string,
    onClose?: () => void
  ): Promise<Viewer3DType> => {
    return new Promise((resolve) => {
      try {
        const viewerElement = document.getElementById('viewer-3d');
        if (access_token == null || viewerElement == null) {
          resolve(null);
          return;
        }

        // Clear any previous canvas left in the container
        viewerElement.innerHTML = '';

        // Always supply a callback token so the viewer can refresh when needed
        const options: any = {
          env: 'AutodeskProduction2',
          getAccessToken: (onTokenReady: (t: string, exp: number) => void) => {
            // Use the current token immediately and refresh in the background when viewer asks again
            try {
              onTokenReady(access_token, 30 * 60);
            } catch {
              onTokenReady(access_token, 10 * 60);
            }
          },
        };

        enableEUAutoRouting();

        try {
          // Tear down any previous instance to avoid residue between file switches
          // @ts-ignore
          Autodesk.Viewing.shutdown && Autodesk.Viewing.shutdown();
        } catch {}

        Autodesk.Viewing.Initializer(options, function () {
          enableEUAutoRouting(); // some builds flip flags during init

          const viewer = new Autodesk.Viewing.GuiViewer3D(viewerElement);
          new ToolbarExtension(viewer, { onClose }).load();

          const rc = viewer.start();
          if (rc !== 0) {
            console.error('[SVFViewer] viewer.start() failed:', rc);
            resolve(null);
            return;
          }

          setViewer3D(viewer);
          resolve(viewer);
        });
      } catch {
        resolve(null);
      }
    });
  };

  /** Load a specific viewable node by guid */
  const loadViewableByGuid = (
    viewer: Autodesk.Viewing.GuiViewer3D,
    guid: string
  ) => {
    if (!viewerDocument) return;
    loadByGuid(viewer, viewerDocument, viewables, guid).catch((e) => {
      console.log(e);
    });
  };

  /** Load viewables for a URN. Let the viewer pick the right backend; no manual `api`. */
  const loadModels = async (
    viewer: Autodesk.Viewing.GuiViewer3D,
    urn: string
  ): Promise<{ ok: boolean; code?: number }> => {
    if (!viewer) return { ok: false };
    if (!urn) return { ok: false };

    let resolved = false;

    return new Promise<{ ok: boolean; code?: number }>((resolve) => {
      try {
        const onDocumentLoadSuccess = (doc: Autodesk.Viewing.Document) => {
          setViewerDocument(doc);

          const list = buildViewables(doc);
          setViewables(list);

          const initial = getInitialNode(doc, list);
          if (!initial) {
            resolved = true;
            resolve({ ok: false });
            return;
          }

          // Small delay helps after quick reopen of the same container
          setTimeout(() => {
            const loadPromise = viewer.loadDocumentNode(doc, initial, {
              keepCurrentModels: false,
            });
            const timeoutPromise = new Promise((_, rej) =>
              setTimeout(
                () => rej(new Error('loadDocumentNode timeout')),
                10000
              )
            );

            Promise.race([loadPromise, timeoutPromise])
              .then(() => {
                try {
                  viewer.resize();
                } catch {}
                resolved = true;
                resolve({ ok: true });
              })
              .catch(() => {
                resolved = true;
                resolve({ ok: false });
              });
          }, 300);
        };

        const onDocumentLoadFailed = (error: any) => {
          const code =
            typeof error === 'number'
              ? error
              : (error && (error.code as number)) || -1;
          if (!resolved) {
            resolved = true;
            resolve({ ok: false, code });
          }
        };

        enableEUAutoRouting();
        Autodesk.Viewing.Document.load(
          `urn:${urn}`,
          onDocumentLoadSuccess,
          onDocumentLoadFailed
        );
      } catch {
        if (!resolved) resolve({ ok: false });
      }
    });
  };

  // ==patch FileVersionsContext for archive/versioned-file sources
  // This updates sidebar/table icons immediately.
  // Tender root attachments do not live in FileVersionsContext, so they skip this path.
  const pushCtxState = (nextState: number) => {
    const fileId = fileToView?.id;
    if (!fileId || !resolvedSource?.canPatchContext) return;

    setFileVersionsContext((prev) => {
      const cur = prev[fileId];
      if (!cur) return prev;

      // if extension_svf_model empty — create mock data,
      // for icon "state" is enough.
      const curExt = cur.extension_svf_model || {
        file_id: String(fileId),
        autodesk_bucket_region: '',
        autodesk_object_id: '',
        state: 0,
      };

      const nextVersion: FileVersionSelection = {
        ...cur,
        extension_svf_model: {
          ...curExt,
          state: nextState,
        },
        // marker that we patched locally
        manual: true,
      };

      return { ...prev, [fileId]: nextVersion };
    });
  };

  // ==POST convert to SVF2
  const transcribe = async () => {
    debugLog('SVF', 'transcribe start', {
      fileName,
      sourceKind: resolvedSource?.kind,
      versionId: version?.id,
      versionedFileId: version?.versioned_file_id,
    });

    try {
      if (!resolvedSource || !version) {
        setStatus({
          loading: false,
          error: true,
          message: intl.formatMessage({ id: 'svf-view-failed-open' }),
          progress: 0,
        });
        return null;
      }

      const transcribeUrl = resolvedSource.getTranscribeUrl();
      if (!transcribeUrl) {
        setStatus({
          loading: false,
          error: true,
          message: intl.formatMessage({ id: 'svf-view-failed-open' }),
          progress: 0,
        });
        return null;
      }

      // paint icon BLUE immediately (queued)
      pushCtxState(1);

      setStatus({
        loading: true,
        error: false,
        message: intl.formatMessage({ id: 'svf-transcribing' }),
        progress: 0,
      });

      const res = await apiCweb.post(transcribeUrl, {});
      console.log('[useSVFViewer][transcribe][job-created]', {
        sourceKind: resolvedSource.kind,
        data: res.data,
      });

      return res.data;
    } catch (error) {
      appLog(error);
      setStatus({
        loading: false,
        error: true,
        message: intl.formatMessage({ id: 'svf-view-failed-transcribe' }),
        progress: 0,
      });
      return null;
    }
  };

  // ==poll conversion status
  const checkTranscribeStatus = async (
    onTick?: (data: TranscribeStatus) => void,
    initialDelay = 1000
  ): Promise<TranscribeStatus | null> => {
    debugLog('SVF', 'status poll start', {
      fileName,
      sourceKind: resolvedSource?.kind,
      versionId: version?.id,
    });

    if (!resolvedSource || !version || !projectId) {
      setStatus({
        loading: false,
        error: true,
        message: intl.formatMessage({ id: 'svf-version-invalid' }),
        progress: 0,
      });
      return null;
    }

    const statusUrl = resolvedSource.getStatusUrl();
    if (!statusUrl) {
      setStatus({
        loading: false,
        error: true,
        message: intl.formatMessage({ id: 'svf-version-invalid' }),
        progress: 0,
      });
      return null;
    }

    abortRef.current = false;

    // Reset fake progress counter when starting new polling session
    fakeProgressCounterRef.current = 0;

    const pollInterval = 5000;

    console.log('[useSVFViewer][status] url', statusUrl);

    return new Promise<TranscribeStatus | null>((resolve) => {
      const poll = async () => {
        if (abortRef.current) {
          resolve(null);
          return;
        }

        try {
          // IMPORTANT:
          // - withCredentials: false → avoids the CORS error when server responds with ACAO: *
          // - validateStatus → we treat 404 as "not ready yet", do NOT throw/redirect
          const res = await apiCweb.get(statusUrl, {
            withCredentials: false,
            validateStatus: () => true,
          });

          // Handle transient 404 (job just created / backend race)
          if (res.status === 404) {
            // keep UI in "in progress" and retry
            if (!abortRef.current) {
              setTimeout(poll, pollInterval);
            }
            return;
          }

          // Any other 4xx/5xx should surface as an error
          if (res.status < 200 || res.status >= 300) {
            throw new Error(`status ${res.status}`);
          }

          const data: TranscribeStatus = res.data;
          try {
            onTick?.(data);
          } catch {}

          console.log('[useSVFViewer][status][tick]', {
            sourceKind: resolvedSource.kind,
            progress: data.translate_svf_progress,
            svf_state: data.svf_model_state,
            queue_stage: data.queue_stage,
          });

          appLog('Transcribe status:', data);

          const queue_stage = data.queue_stage || 1;

          // Calculate improved progress with base stages
          let improvedProgress = 0;
          let message = '';

          // Base progress from completed stages
          if (data.download_source_file_end_at) {
            improvedProgress += 10; // Download completed = 10%
          }
          if (data.upload_source_file_end_at) {
            improvedProgress += 10; // Upload completed = 10%
          }

          // Autodesk conversion progress (remaining 80%)
          const autodeskProgress = data.translate_svf_progress || 0;
          if (autodeskProgress > 0) {
            // If we have real progress from Autodesk, use it for the remaining 80%
            improvedProgress += Math.round((autodeskProgress / 100) * 80);

            // Show special message when nearly complete
            if (autodeskProgress >= 99) {
              message = intl.formatMessage({ id: 'svf-finalizing' });
            } else {
              message =
                queueStages[queue_stage]?.description ||
                intl.formatMessage({ id: 'svf-transcribing' });
            }

            // Reset fake progress counter when we get real progress
            fakeProgressCounterRef.current = 0;
          } else {
            // If Autodesk hasn't started responding yet, show waiting message
            if (data.upload_source_file_end_at) {
              message = intl.formatMessage({ id: 'svf-waiting-for-autodesk' });

              // Implement fake progress increment
              fakeProgressCounterRef.current += 1;
              // Every 2 poll cycles (about 10 seconds at 5s intervals), add 1% fake progress
              // Cap at 20% to provide more visible progress before real progress starts
              const fakeIncrement = Math.min(
                Math.floor(fakeProgressCounterRef.current / 2),
                20
              );
              improvedProgress += fakeIncrement;
            } else {
              message =
                queueStages[queue_stage]?.description ||
                intl.formatMessage({ id: 'svf-transcribing' });
            }
          }

          setStatus({
            loading: true,
            error: false,
            message,
            progress: improvedProgress,
          });

          // reflect state in versions list → icons update (blue/green)
          // Only archive/versioned-file sources live in FileVersionsContext
          const newState = data.svf_model_state;
          pushCtxState(newState);

          if (resolvedSource.canPatchContext) {
            queryCache.setQueryData(
              ['versions', { projectId: String(projectId) }],
              // @ts-ignore
              (prev: any[] | undefined) => {
                if (!Array.isArray(prev)) return prev;
                return prev.map((v) =>
                  v.id === version.id
                    ? {
                        ...v,
                        extension_svf_model: {
                          ...(v.extension_svf_model || {}),
                          state: newState,
                        },
                      }
                    : v
                );
              }
            );
          }

          if (data.translate_svf_progress === 100 && newState === 7) {
            console.log('[useSVFViewer][status][done]', {
              sourceKind: resolvedSource.kind,
              svf_state: newState,
            });
            setStatus({
              loading: true,
              error: false,
              message: intl.formatMessage({
                id: 'svf-view-success-transcribe',
              }),
              progress: 100,
            });
            resolve(data);
          } else if (!abortRef.current) {
            setTimeout(poll, pollInterval);
          }
        } catch (error) {
          if (abortRef.current) {
            resolve(null);
            return;
          }

          // Network / CORS / “Failed to fetch” → treat as transient, keep polling
          const msg = String((error as any)?.message || '');
          if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
            setTimeout(poll, pollInterval);
            return;
          }

          appLog('Transcribe status error:', error);
          setStatus({
            loading: false,
            error: true,
            message: intl.formatMessage({ id: 'svf-transcribe-status-failed' }),
            progress: 0,
          });
          resolve(null);
        }
      };

      console.log('[DEBUG] TranscribeProgress - determining delay ', {
        wasTriggeredByButton: initialDelay === 1000,
        statusLoading: status.loading,
        initialDelay,
      });

      // Add initial delay to give backend time to create the job
      if (initialDelay > 0) {
        setTimeout(() => {
          if (!abortRef.current) {
            poll();
          }
        }, initialDelay);
      } else {
        poll();
      }
    });
  };

  // allow other components to stop polling
  const stopCheck = () => {
    console.log('[useSVFViewer][status][stopCheck]');
    abortRef.current = true;
  };

  // ==resolve URN + access token for Autodesk viewer
  const getUrn = async (): Promise<ViewerData | null> => {
    if (
      fileToView &&
      (fileToView as any).urn &&
      (fileToView as any).access_token
    ) {
      appLog(
        '[useSVFViewer][getUrn] using pre-fetched urn/token from fileToView'
      );

      return {
        urn: (fileToView as any).urn,
        access_token: (fileToView as any).access_token,
      };
    }

    try {
      // Reset stale state when switching files
      setViewerDocument(null);
      setViewables([]);

      if (!resolvedSource || !version) {
        setStatus({
          loading: false,
          error: true,
          message: intl.formatMessage({ id: 'svf-version-invalid' }),
          progress: 0,
        });
        return null;
      }

      const locationUrl = resolvedSource.getLocationUrl();
      if (!locationUrl) {
        setStatus({
          loading: false,
          error: true,
          message: intl.formatMessage({ id: 'svf-version-invalid' }),
          progress: 0,
        });
        return null;
      }

      setStatus({
        loading: true,
        error: false,
        message: intl.formatMessage({ id: 'svf-file-loading-data' }),
        progress: 0,
      });

      const res = await apiCweb.get(locationUrl, {
        headers: resolvedSource.getHeaders ? resolvedSource.getHeaders() : {},
      });

      setStatus({
        loading: false,
        error: false,
        message: '',
        progress: 0,
      });

      return { access_token: res.data.access_token, urn: res.data.urn };
    } catch (error) {
      setStatus({
        loading: false,
        error: true,
        message: intl.formatMessage({ id: 'svf-file-load-data-failed' }),
        progress: 0,
      });
      appLog('Get urn error:', error);
      return null;
    }
  };

  return {
    version,
    status,
    viewer3D,
    extension,
    transcribe,
    initViewer,
    loadModels,
    getUrn,
    checkTranscribeStatus,
    stopCheck,
    setStatus,
    viewables,
    loadViewableByGuid,
  };
};
